import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Bell, ChevronDown } from 'lucide-react';
import { getLenderActor, clearLenderActor, roleLabel, LENDER_BLUE, LENDER_NAVY } from '@/lib/lenderDemo';

const NAV = [
  { key: 'applications', label: 'Applications', path: '/lender/applications' },
  { key: 'loans', label: 'Loans', path: '/lender/loans' },
  { key: 'bankusers', label: 'Bank users', path: '/lender/bank-users', adminOnly: true },
  { key: 'draws', label: 'Draws', path: '/lender/draws' },
  { key: 'inspections', label: 'Inspections', path: null },
  { key: 'audit', label: 'Audit trail', path: null },
];

export default function LenderShell({ active, children }) {
  const navigate = useNavigate();
  const actor = getLenderActor();

  React.useEffect(() => {
    if (!actor) navigate('/lender/demo', { replace: true });
  }, [actor, navigate]);
  if (!actor) return null;

  const isAdmin = actor.role === 'bank_admin';
  const banner =
    actor.role === 'loan_officer'
      ? `Visible to: ${actor.name} only`
      : actor.role === 'construction_dept'
      ? "Every lender's loans across the bank"
      : 'Bank admin · manage users and loan assignments';

  const switchRole = () => { clearLenderActor(); navigate('/lender/demo'); };

  return (
    <div style={{ minHeight: '100vh', background: '#F3F5F9' }}>
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md flex items-center justify-center text-white font-bold text-sm" style={{ background: LENDER_BLUE }}>D</div>
              <span className="font-bold tracking-tight" style={{ color: LENDER_NAVY }}>DrawReady</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{roleLabel(actor.role)}</span>
            </div>
            <nav className="hidden md:flex items-center gap-1">
              {NAV.map((n) => {
                if (n.adminOnly && !isAdmin) return null;
                const isActive = n.key === active;
                const clickable = !!n.path;
                return (
                  <button
                    key={n.key}
                    data-testid={`lender-nav-${n.key}`}
                    onClick={() => clickable && navigate(n.path)}
                    disabled={!clickable}
                    className={`text-sm px-3 py-1.5 rounded-md font-medium transition-colors ${
                      isActive ? 'text-white' : clickable ? 'text-slate-600 hover:bg-slate-50' : 'text-slate-300 cursor-default'
                    }`}
                    style={isActive ? { background: LENDER_BLUE } : undefined}
                  >
                    {n.label}{!clickable && <span className="ml-1 text-[9px] uppercase text-slate-300">soon</span>}
                  </button>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:flex items-center gap-1.5 text-sm text-slate-500"><Building2 className="w-4 h-4" />{actor.bank_name}</span>
            <Bell className="w-4 h-4 text-slate-400" />
            <button onClick={switchRole} data-testid="lender-switch-role" className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-slate-900">
              <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: LENDER_NAVY }}>{actor.name?.[0]}</span>
              {actor.name?.split(' ')[0]}
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="mb-4 inline-flex items-center text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1" data-testid="lender-scope-banner">
          {banner}
        </div>
        {children}
      </main>
    </div>
  );
}

export { LENDER_BLUE, LENDER_NAVY };
